import { useLocation, useNavigate } from "react-router-dom";
import { Home, Search, MessageSquare, Menu, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/layout/AppSidebar";

interface MobileBottomNavProps {
  onQuickAdd: () => void;
  onMoreMenu: () => void;
  onSearch: () => void;
  /** When false the center "Add" FAB is hidden (role can't create anything). */
  canQuickAdd?: boolean;
}

const tabs = [
  { id: "home", label: "Home", icon: Home, path: "/today" },
  { id: "search", label: "Search", icon: Search, path: null },
  { id: "add", label: "Add", icon: Plus, path: null },
  { id: "messages", label: "Messages", icon: MessageSquare, path: "/messaging" },
  { id: "more", label: "More", icon: Menu, path: null },
] as const;

export function MobileBottomNav({ onQuickAdd, onMoreMenu, onSearch, canQuickAdd = true }: MobileBottomNavProps) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // Only show on mobile native or mobile web
  if (!isMobile) return null;

  // Drop the center FAB for roles that can't create anything.
  const visibleTabs = tabs.filter((tab) => tab.id !== "add" || canQuickAdd);

  const isActive = (path: string | null) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const handleTab = (tab: (typeof tabs)[number]) => {
    switch (tab.id) {
      case "search":
        onSearch();
        break;
      case "add":
        onQuickAdd();
        break;
      case "more":
        onMoreMenu();
        break;
      default:
        if (tab.path) navigate(tab.path);
    }
  };

  return (
    <nav
      aria-label="Bottom navigation"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-background print-hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-center justify-around px-2 h-14">
        {visibleTabs.map((tab) => {
          if (tab.id === "add") {
            return (
              <div key={tab.id} className="flex flex-col items-center">
                <button
                  onClick={() => handleTab(tab)}
                  className="relative -mt-5 mb-0.5 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-black shadow-lg shadow-amber-500/30 active:scale-95 transition-transform touch-manipulation [-webkit-tap-highlight-color:transparent]"
                  aria-label="Quick Add"
                >
                  <Plus className="h-7 w-7" strokeWidth={2.5} />
                </button>
                <span className="text-[11px] font-medium leading-none text-muted-foreground">Add</span>
              </div>
            );
          }

          const active = isActive(tab.path);
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => handleTab(tab)}
              className={cn(
                // touch-manipulation: skip the 300ms double-tap-to-zoom
                // deliberation so taps fire click immediately on iOS Safari.
                "relative flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 transition-colors touch-manipulation [-webkit-tap-highlight-color:transparent]",
                active ? "text-primary" : "text-muted-foreground"
              )}
              aria-label={tab.label}
              aria-current={active ? "page" : undefined}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
              </div>
              <span className="text-[11px] font-medium leading-none">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
